/*
 * Профиль на сервере: ник и аватар, которые видят только участники этого сервера. Одно и то же
 * в двух местах — в окне из меню сервера и в своём профиле, открытом с сервера.
 *
 * Ник — по праву CHANGE_NICKNAME (или MANAGE_NICKNAMES), как у Discord. Аватар — право
 * «Кличат Плюс»; убрать его можно всегда, даже если Плюс кончился: иначе картинка осталась бы
 * висеть без возможности снять её.
 */
import { useId, useRef, useState, type FormEvent } from 'react'
import { Trash2, Upload } from 'lucide-react'
import { guildApi } from '@/lib/guildApi'
import { fitAvatar, memberAvatarUrl } from '@/lib/avatar'
import { describeError } from '@/lib/auth'
import { P, basePermissions, has } from '@/lib/permissions'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { Avatar } from '@/components/Avatar'
import { PlusMark } from '@/components/PlusMark'
import layer from '@/components/settings/SettingsLayer.module.css'
import auth from '@/pages/AuthPage.module.css'
import styles from './ServerProfileForm.module.css'

/** Длина ника — как на сервере (guild.applyMemberUpdate) и у Discord. */
export const MAX_NICK = 32

interface Props {
  guildId: string
  /** Уходим в настройки «Плюса»: окно с формой надо закрыть, иначе оно останется поверх. */
  onLeave?: () => void
}

export function ServerProfileForm({ guildId, onLeave }: Props) {
  const user = useAuthStore((s) => s.user)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const me = useGuildStore((s) => (user ? s.members[guildId]?.[user.id] : undefined))
  const applyEvent = useGuildStore((s) => s.applyEvent)
  const openSettings = useAppStore((s) => s.openSettings)
  // черновик ника; null — поле показывает сохранённый ник и следует за ним, если его поменяли с другого устройства
  const [draft, setDraft] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState<'nick' | 'avatar' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const file = useRef<HTMLInputElement>(null)
  const nickId = useId()

  if (!user) return null
  const current = me?.nick ?? ''
  const value = draft ?? current
  const changed = value.trim() !== current
  const profileName = user.global_name ?? user.username
  const name = me?.nick ?? profileName
  // участника может ещё не быть в сторе: тогда считаем по @everyone — сервер всё равно проверит сам
  const perms = guild ? basePermissions(guild, me ?? { roles: [], user: { id: user.id } }) : 0n
  const canNick = has(perms, P.CHANGE_NICKNAME) || has(perms, P.MANAGE_NICKNAMES)
  const plus = user.premium_type === 2

  const apply = (m: Awaited<ReturnType<typeof guildApi.updateSelf>>) => applyEvent('GUILD_MEMBER_UPDATE', { ...m, guild_id: guildId })

  const saveNick = async (e: FormEvent) => {
    e.preventDefault()
    if (!canNick || !changed) return
    setBusy('nick')
    setError(null)
    setSaved(false)
    try {
      apply(await guildApi.updateSelf(guildId, value.trim() || null))
      setDraft(null)
      setSaved(true)
    } catch (err) {
      const d = describeError(err)
      setError(d.fields.nick ?? d.message)
    } finally {
      setBusy(null)
    }
  }

  const saveAvatar = async (avatar: string | null) => {
    setBusy('avatar')
    setError(null)
    try {
      apply(await guildApi.setMemberAvatar(guildId, avatar))
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className={styles.form}>
      <p className={layer.text}>Ник и аватар видят только на этом сервере. В остальных местах остаются имя и аватар из профиля.</p>
      <div className={styles.row}>
        <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={80} src={memberAvatarUrl(guildId, me ?? { avatar: null, user })} decoration={user.avatar_decoration_data} />
        <div className={styles.actions}>
          <span className={auth.label}>Аватар на сервере</span>
          {plus ? (
            <button type="button" className={layer.button} disabled={busy !== null} onClick={() => file.current?.click()}>
              <Upload size={16} /> {busy === 'avatar' ? 'Загружаем…' : 'Выбрать картинку'}
            </button>
          ) : (
            <p className={styles.note}>
              <PlusMark size={16} className={styles.plus} label={null} /> Свой аватар на сервере — в «Кличат Плюс».{' '}
              <button type="button" className={styles.link} onClick={() => { onLeave?.(); openSettings('premium') }}>Подробнее</button>
            </p>
          )}
          {me?.avatar ? (
            <button type="button" className={styles.link} disabled={busy !== null} onClick={() => void saveAvatar(null)}>
              <Trash2 size={14} /> Вернуть обычный
            </button>
          ) : null}
        </div>
      </div>

      <form className={styles.nick} onSubmit={(e) => void saveNick(e)} noValidate>
        <label className={auth.label} htmlFor={nickId}>Ник на сервере</label>
        <div className={styles.nickRow}>
          <input
            id={nickId}
            className={auth.input}
            maxLength={MAX_NICK}
            placeholder={profileName}
            value={value}
            disabled={!canNick || busy === 'nick'}
            onChange={(e) => {
              setDraft(e.target.value)
              setSaved(false)
            }}
          />
          <button type="submit" className={layer.button} disabled={!canNick || !changed || busy !== null}>
            {busy === 'nick' ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
        <span className={auth.hint} aria-live="polite">
          {!canNick ? 'На этом сервере ник меняют только модераторы.' : saved ? 'Ник сохранён.' : `Пусто — будет видно имя из профиля. До ${MAX_NICK} символов.`}
        </span>
      </form>

      <input
        ref={file}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (!f) return
          setBusy('avatar')
          setError(null)
          // ошибки загрузки saveAvatar показывает сам — сюда попадает только картинка, которую не прочитать
          void fitAvatar(f)
            .then((uri) => saveAvatar(uri))
            .catch(() => {
              setError('Не удалось открыть картинку. Подойдёт PNG, JPEG, WebP или GIF.')
              setBusy(null)
            })
        }}
      />
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
    </div>
  )
}
